import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { AuthService } from '@/lib/auth-service';
import { TemporalAuraAuthenticator } from './TemporalAuraAuthenticator';
import { PureHeartGuardian } from './PureHeartGuardian';

interface User {
  id: string;
  name: string;
  email?: string;
  role: string;
  auraSignature?: any;
  intentionSignature?: any;
  verified?: boolean;
  accessLevel?: string;
}

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<boolean>;
  signup: (email: string, password: string, name: string) => Promise<boolean>;
  logout: () => void;
  loading: boolean;
  showAuraAuth: boolean;
  showHeartGuard: boolean;
  setShowAuraAuth: (show: boolean) => void;
  setShowHeartGuard: (show: boolean) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [showAuraAuth, setShowAuraAuth] = useState(false);
  const [showHeartGuard, setShowHeartGuard] = useState(false);

  useEffect(() => {
    // Check for existing session on mount
    const existingUser = localStorage.getItem('nexus_user');
    if (existingUser) {
      setUser(JSON.parse(existingUser));
    }
    setLoading(false);
  }, []);

  const login = async (email: string, password: string) => {
    // For traditional login, redirect to aura authentication
    setShowAuraAuth(true);
    return false;
  };

  const signup = async (email: string, password: string, name: string) => {
    // For signup, redirect to pure heart guardian
    setShowHeartGuard(true);
    return false;
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('nexus_user');
    setShowAuraAuth(false);
    setShowHeartGuard(false);
  };

  const handleAuraAuthenticated = (userData: any) => {
    setUser(userData);
    localStorage.setItem('nexus_user', JSON.stringify(userData));
    setShowAuraAuth(false);
  };

  const handleGuardianVerified = (guardianData: any) => {
    setUser(guardianData);
    localStorage.setItem('nexus_user', JSON.stringify(guardianData));
    setShowHeartGuard(false);
  };

  const value: AuthContextType = {
    user,
    login,
    signup,
    logout,
    loading,
    showAuraAuth,
    showHeartGuard,
    setShowAuraAuth,
    setShowHeartGuard
  };

  if (showAuraAuth) {
    return (
      <AuthContext.Provider value={value}>
        <TemporalAuraAuthenticator onAuthenticated={handleAuraAuthenticated} />
      </AuthContext.Provider>
    );
  }

  if (showHeartGuard) {
    return (
      <AuthContext.Provider value={value}>
        <PureHeartGuardian onGuardianVerified={handleGuardianVerified} />
      </AuthContext.Provider>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};